// Temporal analysis UI in each version's card of the 素材 tab, and event markers on the timeline (F-03.1, F-03.3).

import type { App } from '../app/app';
import type { SourceId } from '../core/types';
import { TemporalManager } from '../analysis/temporal-manager';
import { tickOfAFrame } from '../core/timeline';
import { t } from '../i18n';
import { $, esc, fmtSigned } from './dom';

let tm: TemporalManager;
let lastKey = '';
/** Rendered frame-check part per version; the 素材 cards hold a slot for it. */
const slotHtml = new Map<SourceId, string>();

/** Puts each version's frame-check part into its card (after the cards were rebuilt, or when it changed). */
export function refreshTemporalSlots(): void {
  for (const el of document.querySelectorAll<HTMLElement>('#sourceCards [data-temporal]')) {
    const html = slotHtml.get(el.dataset.temporal as SourceId) ?? '';
    if (el.innerHTML !== html) el.innerHTML = html;
  }
}

export function initTemporal(app: App): void {
  tm = new TemporalManager(app);
  (app as unknown as { temporal: TemporalManager }).temporal = tm;
  tm.onChange = () => app.store.notify();
  $('#sourceCards').addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
    if (!b || !b.dataset.src) return;
    const id = b.dataset.src as SourceId;
    if (b.dataset.act === 'analyse') void tm.analyse(id);
    if (b.dataset.act === 'cancel') tm.cancel();
    if (b.dataset.act === 'apply') {
      const r = tm.results.get(id);
      if (r?.offset) app.setOffsetAbs(id, r.offset.offset);
    }
    if (b.dataset.act === 'jump') {
      if (app.player.playing) app.player.pause();
      app.player.seek(tickOfAFrame(app.sources.timeline, Number(b.dataset.a)));
    }
  });
}

export function updateTemporal(app: App): void {
  if (!tm) return;
  const bs = app.sources.loadedBs();
  const key = JSON.stringify([
    bs,
    tm.running,
    Math.round(tm.progress * 50),
    tm.error,
    [...tm.results.values()].map((r) => [r.source, r.offset?.offset, r.events.length]),
    bs.map((id) => app.sources.source(id)?.frameOffset),
    app.sources.timeline.totalTicks,
    t('temporal.title'),
  ]);
  if (key === lastKey) return;
  lastKey = key;
  slotHtml.clear();
  bs.forEach((id, i) => {
      const src = app.sources.source(id)!;
      const r = tm.results.get(id);
      let body = '';
      if (tm.running === id)
        body = `<div class="muted small">${esc(t('temporal.running', { p: Math.round(tm.progress * 100) }))}</div><button data-act="cancel" data-src="${id}">${esc(t('common.cancel'))}</button>`;
      else if (r) {
        if (r.offset) {
          const same = r.offset.offset === src.frameOffset;
          const conf = r.offset.confidence;
          body += `<div><b>${esc(t('temporal.suggest', { id, n: fmtSigned(r.offset.offset) }))}</b> <span class="muted small">${esc(t('temporal.confidence', { c: Number.isFinite(conf) ? conf.toFixed(1) : '∞' }))}</span></div>`;
          if (conf < 1.3) body += `<div class="muted small">${esc(t('temporal.lowConfidence'))}</div>`;
          body += same ? `<div class="muted small">${esc(t('align.applied'))}</div>` : `<div><button class="primary" data-act="apply" data-src="${id}">${esc(t('temporal.apply'))}</button></div>`;
        } else body += `<div class="muted small">${esc(t('temporal.none'))}</div>`;
        body += `<div><button data-act="analyse" data-src="${id}">${esc(t('temporal.again'))}</button></div>`;
      }
      // Not checked yet: the card's "Check frames" button is all there is (D-48).
      // The analysis error (if any) is shown once, on the first version's card.
      slotHtml.set(id, (i === 0 && tm.error ? `<div class="warn-item">${esc(tm.error)}</div>` : '') + (body ? `<div class="temporal">${body}</div>` : ''));
  });
  refreshTemporalSlots();
}
